<template>
  <div id="contact-form" :class="globalClassNames">
    <div class="container">
      <h2 v-if="titleProps" class="c-landing-page-contact__title">{{ titleProps }}</h2>
      <Component :is="containerComponent" :class="wrapperClassNames" data-test-wrapper>
        <div class="c-landing-page-contact__form-wrapper">
          <div :id="targetId" data-test-hubspot-target></div>
        </div>
        <div :class="['c-landing-page-contact__contact-wrapper', { 'c-landing-page-contact__icon--blue': blueIcon }]">
          <template v-if="contactLinks.length === 1 && !contactLinks[0]" />
          <ul v-else-if="contactLinks.length" class="c-landing-page-contact__social-contacts">
            <li v-for="(contactLink, index) in contactLinks" :key="index">
              <a :href="`${contactLink.action}:${contactLink.name}`">
                <span :class="`adn-icon adn-icon--${contactLink.iconName}`" aria-hidden="true" />
                <span class="c-landing-page-contact--link-text">{{ contactLink.name }}</span>
              </a>
            </li>
          </ul>
          <ul v-if="socialLinks.length" class="c-landing-page-contact__social-networks">
            <li v-for="(socialLink, index) in socialLinks" :key="index">
              <a :href="socialLink.link" target="_blank">
                <span :class="`adn-icon adn-icon--${socialLink.iconName}`" aria-hidden="true" />
                <span class="c-landing-page-contact--link-text">{{ socialLink.name }}</span>
              </a>
            </li>
          </ul>
        </div>
      </Component>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, onBeforeMount, onMounted, ref, toRefs, type PropType } from 'vue'
import { useHubspot } from '@b2ag/plugin-hubspot/dist/cjs/composables/useHubspot'
import { useAnalyticService } from '@b2ag/plugin-aladin-gtm'
import { emitAnalyticsEvent } from '@b2ag/micro-front-tools'

export default defineComponent({
  name: 'CFormContact',
  props: {
    title: {
      type: String,
      default: '',
    },
    formId: {
      type: String,
      required: true,
    },
    socialLinks: {
      type: Array as PropType<Array<{ name: string; iconName: string; action: string; link: string }>>,
      default: () => [],
    },
    contactLinks: {
      type: Array as PropType<Array<{ name: string; iconName: string; action: string }>>,
      default: () => [],
    },
    blueIcon: {
      type: Boolean,
      default: false,
    },
    targetId: {
      type: String,
      required: true,
    },
    portalId: {
      type: String,
      required: true,
    },
    formOnly: {
      type: Boolean,
      default: false,
    },
    submittedEvent: {
      type: String,
      default: 'LP_contact_form',
    },
  },
  setup(props) {
    const { createHubspotForm, unmountReactComponent, initHbFormListener, removeHbFormListener } = useHubspot()
    const isShown = ref(false)
    const {
      formOnly,
      submittedEvent,
      contactLinks,
      socialLinks,
      title: titleProps,
      formId,
      targetId,
      portalId,
    } = toRefs(props)

    const containerComponent = 'div'
    const globalClassNames = computed(() => {
      const classes = ['c-landing-page-contact']
      if (formOnly.value) {
        classes.push('c-landing-page-contact--formOnly')
      }
      return classes
    })
    const wrapperClassNames = computed(() => {
      const classes = ['c-landing-page-contact__content-wrapper']
      if ((!contactLinks.value.length && !socialLinks.value.length) || formOnly.value) {
        classes.push('c-landing-page-contact__content-wrapper--noLinks')
      }
      return classes
    })

    function onFormSubmitted(): void {
      if (!emitAnalyticsEvent({ eventName: submittedEvent.value, payload: {} }))
        useAnalyticService().trackEvent(submittedEvent.value, {})
    }

    onBeforeMount(() => {
      initHbFormListener({
        formId: formId.value,
        onFormSubmitted,
      })
    })

    onMounted(() => {
      if (!isShown.value) {
        createHubspotForm({
          formId: formId.value,
          targetId: targetId.value,
          portalId: portalId.value,
          createdCallBack: undefined,
        })
        isShown.value = true
      }
    })

    onBeforeUnmount(() => {
      unmountReactComponent(formId.value)
      removeHbFormListener()
    })

    return {
      containerComponent,
      globalClassNames,
      wrapperClassNames,
      onFormSubmitted,
      titleProps,
    }
  },
})
</script>

<style lang="scss">
$container-padding: 1.5rem;

.hs-form-iframe {
  width: 100% !important;
}

.c-landing-page-contact {
  padding: 4rem 0;

  .container {
    padding-left: 0;
    padding-right: 0;
  }

  @media all and (min-width: $breakpoint-lg) {
    padding: 0 0 6rem;
  }
}

.c-landing-page-contact__title {
  display: inline-block;
  color: transparent;

  @include apply-linear-gradient((var(--gradient-main-start), var(--gradient-main-end)), 90deg);

  background-clip: text;
  font-size: 2.8rem;
  margin-bottom: var(--spacing-3);
  white-space: pre-line;
  padding-left: $container-padding;
  padding-right: $container-padding;

  @media all and (min-width: $breakpoint-lg) {
    margin-bottom: calc(2 * #{var(--spacing-3)});
    padding-left: 0;
    padding-right: 0;
  }
}

.c-landing-page-contact__content-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;

  .c-landing-page-contact__contact-wrapper {
    margin: 0 auto;

    @media all and (min-width: $breakpoint-sm) {
      margin: 0;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
    }

    @media all and (min-width: $breakpoint-lg) {
      display: block;
    }
  }

  .c-landing-page-contact__form-wrapper .submitted-message {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    min-height: 10rem;
    background-color: var(--color-white);
    padding: var(--spacing-3);
    margin-bottom: var(--spacing-3);

    p {
      position: relative;
    }

    p:first-child::before {
      content: '';
      display: inline-block;
      position: relative;
      top: 0;
      left: 0.7rem;
      width: 0.6rem;
      height: 1.2rem;
      border: solid var(--color-white);
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
      margin-right: 2rem;
      z-index: 3;
    }

    p:first-child::after {
      content: '';
      display: inline-block;
      position: absolute;
      top: 2px;
      width: 2rem;
      height: 2rem;
      margin-right: 1rem;
      border-radius: 50%;
      background-color: var(--color-success);
      left: 0;
    }

    @media all and (min-width: $breakpoint-lg) {
      height: auto;
      border-radius: var(--border-radius-2);
      box-shadow: 0 10px 20px rgb(0 0 0 / 24%);
      margin-bottom: 0;
    }
  }

  @media all and (min-width: $breakpoint-lg) {
    flex-direction: row;
    justify-content: space-between;

    .c-landing-page-contact__form-wrapper {
      position: relative;
      order: 2;
      width: calc((8 * 100%) / 12);
      margin-top: var(--form-offset);
    }

    .c-landing-page-contact__contact-wrapper {
      order: 1;
      width: calc((4 * 100%) / 12);
    }

    &.c-landing-page-contact__content-wrapper--noLinks {
      .c-landing-page-contact__form-wrapper {
        width: 100%;
      }

      .c-landing-page-contact__contact-wrapper {
        display: none;
      }
    }
  }

  & .c-landing-page-contact__icon--blue {
    .adn-icon {
      color: var(--color-main);
    }
  }
}

.c-landing-page-contact__social-contacts,
.c-landing-page-contact__social-networks {
  padding-left: $container-padding;
  padding-right: $container-padding;

  @media all and (min-width: $breakpoint-lg) {
    padding-left: 0;
    padding-right: 0;
  }

  @media all and (min-width: $breakpoint-lg) {
    padding-top: 0;
  }

  li {
    padding-bottom: var(--spacing-3);

    @media all and (min-width: $breakpoint-lg) {
      padding-bottom: 1.2rem;
    }
  }

  a {
    color: var(--color-black);
  }

  a:hover .c-landing-page-contact--link-text {
    color: var(--color-main);
    text-decoration: underline;
  }

  span {
    margin-right: 6px;
  }
}

.c-landing-page-contact__social-contacts {
  padding-bottom: 0;

  @media (min-width: $breakpoint-lg) {
    padding-bottom: calc(2 * #{var(--spacing-3)});
  }
}

.c-landing-page-contact--youtube {
  display: inline-block;
  height: 1.4rem;
  width: 2rem;
  background-image: url('./lp_farmers_contact_youtube.svg');
  background-position: center;
  background-repeat: no-repeat;
}

.c-landing-page-contact .hs-form {
  padding: 4rem 4rem 3rem;
  border-radius: var(--border-radius-2);
  background-color: white;
  margin-bottom: var(--spacing-3);

  @media (max-width: $breakpoint-sm) {
    padding: 2rem;
    padding-top: 0;
  }

  @media all and (min-width: $breakpoint-lg) {
    box-shadow: 0 10px 20px rgb(0 0 0 / 24%);
  }

  fieldset {
    max-width: none !important;
  }

  label {
    cursor: pointer;
  }

  .input {
    margin-right: 0 !important;
  }

  input.hs-input:not([type='checkbox']) {
    width: 100% !important;
  }

  input[type='checkbox'].hs-input {
    border: 1px solid #0076b4;
    box-sizing: border-box;
    border-radius: 2px;
    height: 2rem;
    width: 2rem;
    margin-top: 3px;
  }

  .legal-consent-container .hs-form-booleancheckbox-display > span {
    margin-left: 2.7rem;
    font-size: 1.4rem;
  }

  @media (width >= 481px) {
    .hs_firstname {
      padding-right: 10px;
    }
  }

  .field {
    margin-top: 2rem;
  }

  .hs-form-required {
    color: var(--color-error);
  }

  .hs-error-msg {
    color: var(--color-error);
  }

  .hs-error-msgs li {
    margin-top: 0.5rem;
  }

  input.invalid,
  input.error {
    border-color: var(--color-error);
  }

  textarea {
    min-height: 10rem;
  }

  .hs_submit {
    margin-top: 2rem;
    text-align: right;
  }

  .hs-button {
    cursor: pointer;
    border: solid 1px transparent;
    border-radius: var(--border-radius-1);
    min-height: 4.4rem;
    display: inline-block;
    font-size: 1.6rem;
    font-weight: bold;
    line-height: 4.2rem;
    padding: 0 2rem;
    transition: all 0.1s ease-in;
    box-sizing: border-box;
    outline: none;
    letter-spacing: 0.3px;
    text-align: center;
    background-color: var(--color-main);
    color: white;
    width: 100%;

    @media (min-width: $breakpoint-sm) {
      width: auto;
    }

    &:hover {
      box-shadow: 0 10px 20px rgb(65 82 92 / 20%);
      background-color: var(--color-main-dark);
      color: white;
    }

    &:active {
      box-shadow: none;
      background-color: var(--color-main);
      color: white;
    }

    &:focus {
      box-shadow: 0 10px 20px rgb(65 82 92 / 20%);
      background-color: var(--color-main-dark);
      border-color: var(--color-main-dark);
      color: white;
    }

    &:disabled {
      box-shadow: none;
      cursor: not-allowed;
      background-color: var(--color-inactive-light);
      color: var(--color-text-light);
    }

    &[disabled] {
      box-shadow: none;
      cursor: not-allowed;
      background-color: var(--color-inactive-light);
      color: var(--color-text-light);
    }
  }
}
</style>
